import { Fragment } from 'react'
import { useLocation, useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
// Models
import { IConversation } from 'app/models/ChatAI';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { updateConversation, deleteConversation } from 'app/store/AIChat/AIChat.async';
// Selectors
import { selectLoading } from 'app/store/AIChat/AIChat.selectors';
// Mui
import { Theme } from '@mui/material';
// Icons
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
// Dialogs
import ConfirmationDialog from 'app/dialogs/ConfirmationDialog';
import ChatRenameFormDialog from './ChatRenameFormDialog';
// Components
import { IconButton } from 'app/components/Mui';
// Hooks
import useMenu from 'app/hooks/useMenu';
import useToggle from 'app/hooks/useToggle';

type Props = {
  conversation: IConversation;
}

export default function ChatListItemDropdown({ conversation }:Props) {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const history = useHistory();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const loading = useAppSelector(selectLoading);

  const { Menu, MenuItem, openMenu } = useMenu();
  const { open, toggle } = useToggle();
  const { open:openConfirmationDialog, toggle:toggleConfirmationDialog } = useToggle();

  const handleUpdatePrivate = (isPrivate:boolean) => async () => {
    const { id, ...restConversationData } = conversation;
    try {
      await dispatch(updateConversation({
        conversationId: id,
        data: { ...restConversationData, private: isPrivate }
      })).unwrap();
    } catch (error) {
      console.error('Failed to update conversation:', error);
    }
  }

  const handleConfirm = async () => {
    try {
      await dispatch(deleteConversation(conversation.id)).unwrap();
      const conversationPath = `/${conversation.id}`;
      if ( pathname.endsWith(conversationPath) ){
        history.replace(pathname.replace(conversationPath, ''));
      }
    } catch(error){}
  }

  return (
    <Fragment>
      <IconButton
        name={`Chat list item dropdown ${conversation.id}`}
        aria-label="Chat list item dropdown button"
        onClick={openMenu}
        edge="end"
      ><MoreVertIcon /></IconButton>
      <Menu>
        <MenuItem
          name={`Rename conversation ${conversation.id}`}
          onClick={toggle}
        >{t('buttons.rename')}</MenuItem>
        <MenuItem
          name={`Make ${conversation.private ? 'Public' : 'Private'} conversation ${conversation.id}`}
          onClick={handleUpdatePrivate(!conversation.private)}
        >{conversation.private ? t('buttons.makePublic') : t('buttons.makePrivate')}</MenuItem>
        <MenuItem
          name={`Delete conversation ${conversation.id}`}
          sx={{
            color: (theme:Theme) => theme.palette.error.main
          }}
          onClick={toggleConfirmationDialog}
        >{t('buttons.delete')}</MenuItem>
      </Menu>
      {open ? (
        <ChatRenameFormDialog
          open={open}
          onClose={toggle}
          conversation={conversation}
        />
      ) : null}
      {openConfirmationDialog ? (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={toggleConfirmationDialog}
          loading={loading}
          title={t('dialogs.confirmation.deleteTitle', { dataType: t('labels.conversation') })}
          content={(
            <Trans
              t={t} i18nKey="dialogs.confirmation.deleteConfirmationWithName"
              components={{ strong: <strong /> }}
              values={{ dataType: t('labels.conversation'), dataName: conversation.name }}
            />
          )}
          onConfirm={handleConfirm}
        />
      ) : null}
    </Fragment>
  )
};
